// Meta Pixel tracking utility
// Centralizes all fbq calls for consistency across dual Pixel IDs

type FbqParams = Record<string, unknown>;

const fbq = (action: string, event: string, params?: FbqParams) => {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    if (params) {
      (window as any).fbq(action, event, params);
    } else {
      (window as any).fbq(action, event);
    }
  }
};

// Standard events
export const trackPageView = () => fbq('track', 'PageView');

export const trackLead = (contentName: string, contentCategory: string) =>
  fbq('track', 'Lead', { content_name: contentName, content_category: contentCategory });

export const trackInitiateCheckout = (contentName: string, value: number, currency = 'USD') =>
  fbq('track', 'InitiateCheckout', { content_name: contentName, value, currency });

export const trackPurchase = (value: number, contentName: string, currency = 'USD') =>
  fbq('track', 'Purchase', { value, currency, content_type: 'product', content_name: contentName });

export const trackViewContent = (contentName: string, contentCategory?: string) =>
  fbq('track', 'ViewContent', {
    content_name: contentName,
    ...(contentCategory && { content_category: contentCategory }),
  });

// Custom events for funnel tracking
export const trackCustomEvent = (eventName: string, params?: FbqParams) =>
  fbq('trackCustom', eventName, params);

// CTA click tracking — fires ViewContent with button context
export const trackCTAClick = (buttonName: string, section: string) =>
  fbq('track', 'ViewContent', {
    content_name: buttonName,
    content_category: section,
  });

// Advanced matching — re-init with user data
export const setAdvancedMatching = (data: { email?: string; country?: string }) => {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    const matchData: Record<string, string> = {};
    if (data.email) matchData.em = data.email.toLowerCase().trim();
    if (data.country) matchData.country = data.country.toLowerCase().replace(/\s+/g, '');
    (window as any).fbq('init', '933872596268826', matchData);
    (window as any).fbq('init', '778185344689672', matchData);
  }
};

// Google Ads gtag helper
const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag(...args);
  }
};

// Google Ads conversion events
export const trackGoogleAdsConversion = (conversionLabel: string, value?: number, currency = 'USD') => {
  gtag('event', 'conversion', {
    send_to: `AW-17971913400/${conversionLabel}`,
    ...(value !== undefined && { value, currency }),
  });
};

// Google Ads begin_checkout event
export const trackGoogleAdsBeginCheckout = (value: number, currency = 'USD') => {
  gtag('event', 'begin_checkout', { value, currency });
};

export const trackGoogleAdsSignup = (method: string) => {
  gtag('event', 'sign_up', { method });
};

export const trackGoogleAdsPurchase = (value: number, currency = 'USD', transactionId?: string) => {
  // Standard purchase event
  gtag('event', 'purchase', {
    value,
    currency,
    ...(transactionId && { transaction_id: transactionId }),
  });
  // Google Ads conversion event for "Compra (1)"
  gtag('event', 'conversion', {
    send_to: 'AW-17971913400/VLtgCJbdgf4bELjF1vlC',
    value,
    currency,
    ...(transactionId && { transaction_id: transactionId }),
  });
};

// Taboola Pixel helpers
const tfa = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && (window as any)._tfa) {
    (window as any)._tfa.push({ notify: 'event', name: eventName, id: 1998877, ...params });
  }
};

export const trackTaboolaPageView = () => tfa('page_view');
export const trackTaboolaLead = () => tfa('lead');
export const trackTaboolaPurchase = (revenue?: number) =>
  tfa('purchase', revenue !== undefined ? { revenue } : undefined);
export const trackTaboolaStartCheckout = () => tfa('start_checkout');
export const trackTaboolaViewContent = () => tfa('view_content');
